import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Input } from 'components/atoms';
import CitySelect from 'components/molecules/CitySelect/CitySelect';
import CountrySelect from 'components/molecules/CountrySelect/CountrySelect';
import DepartmentSelect from 'components/molecules/DepartmentSelect/DepartmentSelect';
import PhoneCodeSelect from 'components/molecules/PhoneCodeSelect/PhoneCodeSelect';
import { Section } from 'components/molecules/TabPanel/TabPanel';
import { useExternalCodeContext } from 'utils/helpers/ExternalCodesContext';
import { formTypes } from 'utils/interfaces';

import { useProfileFormContext } from '../hooks/useProfileFormContext';

import './TabContactProfile.component.scss';

interface ITabContactProfileProps {
  formValues: any;
  isDisabled: boolean;
  changeData: (value: { name: formTypes; value?: number | string }) => Promise<void>;
  isEditing: boolean;
}

export const TabContactProfile = ({
  formValues,
  isDisabled,
  changeData,
  isEditing,
}: ITabContactProfileProps) => {
  const { email, profileData } = useProfileFormContext();
  const {
    register,
    getValues,
    control,
    formState: { errors },
    trigger,
    setValue,
  } = useFormContext();

  const {
    countryOfResidence,
    departmentOfResidence,
    setCountryOfResidence,
    setDepartmentOfResidence,
    countryStatesCode,
    countryCitiesCode,
  } = useExternalCodeContext();

  const country = useWatch({ control, name: 'country' });

  const extractPhoneCode = (fullPhoneNumber: string): string => {
    if (!fullPhoneNumber || !fullPhoneNumber.startsWith('+')) return '';

    const match = fullPhoneNumber.match(/^\+(\d{1,4})/);
    return match ? `+${match[1]}` : '';
  };

  const extractPhoneNumber = (fullPhoneNumber: string, phoneCode: string): string => {
    if (!fullPhoneNumber || !phoneCode) return fullPhoneNumber || '';

    if (fullPhoneNumber.startsWith(phoneCode)) {
      return fullPhoneNumber.substring(phoneCode.length);
    }

    return fullPhoneNumber;
  };

  const combinePhoneNumber = (phoneCode: string, phoneNumber: string): string => {
    if (!phoneCode || !phoneNumber) return phoneNumber || '';

    if (phoneNumber.startsWith(phoneCode)) {
      return phoneNumber;
    }

    return `${phoneCode}${phoneNumber}`;
  };

  const fillDefaultData = () => {
    if (profileData) {
      if (!formValues.email && email) {
        changeData({ name: 'email', value: email });
      }

      if (!formValues.direction && profileData.direction) {
        changeData({ name: 'direction', value: profileData.direction });
      }

      if (!formValues.country && profileData.country) {
        changeData({ name: 'country', value: profileData.country });
      }

      if (!formValues.state && profileData.state) {
        changeData({ name: 'state', value: profileData.state });
      }

      if (!formValues.city && profileData.city) {
        changeData({ name: 'city', value: profileData.city });
      }
    }
  };

  const separateAndSetPhoneData = () => {
    if (!profileData) return;

    let phoneCode = '';
    let phoneNumberOnly = '';
    let fullPhoneNumber = '';

    if (profileData.phoneCode) {
      phoneCode = profileData.phoneCode.startsWith('+')
        ? profileData.phoneCode
        : `+${profileData.phoneCode}`;
    }

    if (profileData.phoneNumber) {
      if (profileData.phoneNumber.startsWith('+')) {
        fullPhoneNumber = profileData.phoneNumber;

        if (!phoneCode) {
          phoneCode = extractPhoneCode(profileData.phoneNumber);
        }

        phoneNumberOnly = extractPhoneNumber(profileData.phoneNumber, phoneCode);
      } else {
        phoneNumberOnly = profileData.phoneNumber;
        fullPhoneNumber = phoneCode ? `${phoneCode}${phoneNumberOnly}` : phoneNumberOnly;
      }
    }

    if (phoneCode && (!formValues.phoneCode || formValues.phoneCode !== phoneCode)) {
      changeData({ name: 'phoneCode', value: phoneCode });
      setValue('phoneCode', phoneCode, { shouldValidate: true });
    }

    if (fullPhoneNumber && formValues.phoneNumber !== fullPhoneNumber) {
      changeData({ name: 'phoneNumber', value: fullPhoneNumber });
      setValue('phoneNumber', fullPhoneNumber, { shouldValidate: true });
    }
  };

  const getDisplayPhoneNumber = (): string => {
    if (!formValues.phoneNumber) return '';

    const phoneCode = formValues.phoneCode || '';
    return extractPhoneNumber(formValues.phoneNumber, phoneCode);
  };

  useEffect(() => {
    if (profileData) {
      fillDefaultData();
      separateAndSetPhoneData();
    }
  }, [profileData]);

  useEffect(() => {
    if (formValues.country && profileData?.country === formValues.country) {
      const countryData = document.querySelector(`[data-value="${formValues.country}"]`);
      if (countryData) {
        const externalCode = countryData.getAttribute('data-external-code');
        if (externalCode) {
          setCountryOfResidence(externalCode);
        }
      }
    }
  }, [formValues.country, profileData]);

  return (
    <Section id={1}>
      <section className='section-form'>
        <aside className='section-form__aside'>
          <Input
            id='email'
            label='Correo electrónico'
            type='email'
            placeholder='usuario@gmail.com'
            value={formValues.email}
            disabled={!isEditing}
            register={register}
            className={isEditing ? 'input_email' : ''}
            labelClassName={isEditing ? 'label_email' : ''}
          />
          <article className='section-form__article'>
            <div className='phone-input-container'>
              <Controller
                control={control}
                name='phoneCode'
                render={({ field }) =>
                  isDisabled ? (
                    <Input
                      id='phoneCode'
                      label='Celular'
                      placeholder=''
                      type='text'
                      value={formValues.phoneCode || ''}
                      disabled
                    />
                  ) : (
                    <PhoneCodeSelect
                      textLabel='Celular'
                      htmlForLabel='phoneCode'
                      placeholder='Código'
                      value={{
                        label: field.value ? field.value : '',
                        value: field.value ? field.value : '',
                      }}
                      handleChange={(value) => {
                        const newPhoneCode = value || '';
                        field.onChange(newPhoneCode);
                        changeData({ name: 'phoneCode', value: newPhoneCode });

                        const currentDisplayNumber = getDisplayPhoneNumber();
                        if (currentDisplayNumber) {
                          const newFullNumber = combinePhoneNumber(
                            newPhoneCode,
                            currentDisplayNumber
                          );
                          changeData({ name: 'phoneNumber', value: newFullNumber });
                          setValue('phoneNumber', newFullNumber, { shouldValidate: true });
                        }
                      }}
                      disabled={isDisabled}
                      defaultValue={profileData?.phoneCode}
                      error={!!errors?.phoneCode}
                    />
                  )
                }
              />
              <Controller
                control={control}
                name='phoneNumber'
                render={({ field }) => (
                  <Input
                    id='phoneNumber'
                    label=''
                    placeholder='Escribe tu celular'
                    type='tel'
                    value={getDisplayPhoneNumber()}
                    onChange={(e) => {
                      const { value } = e.currentTarget;
                      const phoneCode = formValues.phoneCode || '';

                      const fullPhoneNumber = combinePhoneNumber(phoneCode, value);

                      field.onChange(fullPhoneNumber);

                      changeData({ name: 'phoneNumber', value: fullPhoneNumber });
                    }}
                    disabled={isDisabled}
                    error={!!errors?.phoneNumber}
                  />
                )}
              />
            </div>
          </article>
        </aside>

        <aside className='section-form__aside'>
          <article className='section-form__article'>
            <Input
              id='direction'
              label='Dirección de residencia'
              placeholder='Tú dirección'
              value={formValues.direction}
              onChange={(e) => {
                const { value } = e.currentTarget;
                changeData({ name: 'direction', value });
              }}
              disabled={isDisabled}
              subtext={
                isEditing ? 'Especifica: calle / carrera / transversal / diagonal / avenida' : ''
              }
              error={!!errors.direction}
            />
          </article>

          <article className='section-form__article'>
            <Controller
              control={control}
              name='country'
              render={({ field: { onChange, value, ...field }, fieldState: { error } }) =>
                isDisabled ? (
                  <Input
                    id='country'
                    label='País residencia'
                    placeholder=''
                    type='text'
                    value={formValues.country}
                    disabled
                  />
                ) : (
                  <CountrySelect
                    textLabel='País residencia'
                    htmlForLabel='country'
                    placeholder='Seleccione uno'
                    error={!!errors.country}
                    subtext={error?.message}
                    handleChange={(value) => {
                      const { country } = getValues();
                      if (value != country) {
                        setValue('state', '');
                        setValue('city', '');
                      }
                      trigger(['state', 'city']);
                      onChange(value);
                      changeData({ name: 'country', value });
                    }}
                    value={{
                      label: value as any,
                      value: value as any,
                    }}
                    onExternalCodeChange={(externalCode) => {
                      setCountryOfResidence(externalCode);
                    }}
                    {...field}
                    defaultValue={profileData?.country}
                  />
                )
              }
            />
          </article>
        </aside>

        <aside className='section-form__aside'>
          <article className='section-form__article'>
            <Controller
              control={control}
              name='state'
              render={({ field: { onChange, value, ...field }, fieldState: { error } }) =>
                isDisabled ? (
                  <Input
                    id='state'
                    label='Departamento'
                    placeholder=''
                    type='text'
                    value={formValues.state}
                    disabled
                  />
                ) : (
                  <DepartmentSelect
                    textLabel='Departamento'
                    htmlForLabel='state'
                    placeholder='Seleccione uno'
                    key='state'
                    subtext={error?.message}
                    error={!!errors.department}
                    handleChange={(value) => {
                      const { country } = getValues();
                      if (value != country) {
                        setValue('city', '');
                      }
                      trigger(['city']);
                      onChange(value);
                      changeData({ name: 'state', value });
                    }}
                    value={{
                      label: value as any,
                      value: value as any,
                    }}
                    catalogName={countryStatesCode}
                    disabled={!countryOfResidence}
                    country={countryOfResidence}
                    onExternalCodeChange={(externalCode) => {
                      setDepartmentOfResidence(externalCode);
                    }}
                    {...field}
                    defaultValue={profileData?.state}
                  />
                )
              }
            />
          </article>

          <article className='section-form__article'>
            <Controller
              control={control}
              name='city'
              render={({ field: { onChange, value, ...field }, fieldState: { error } }) =>
                isDisabled ? (
                  <Input
                    id='city'
                    label='Ciudad'
                    placeholder=''
                    type='text'
                    value={formValues.city}
                    disabled
                  />
                ) : (
                  <CitySelect
                    textLabel='Ciudad'
                    htmlForLabel='city'
                    key='city'
                    subtext={error?.message}
                    placeholder='Seleccione uno'
                    error={!!errors.city}
                    handleChange={(value) => {
                      onChange(value);
                      changeData({ name: 'city', value });
                    }}
                    value={{
                      label: value as any,
                      value: value as any,
                    }}
                    father={departmentOfResidence}
                    catalogName={countryCitiesCode}
                    disabled={!departmentOfResidence}
                    department={departmentOfResidence}
                    {...field}
                    defaultValue={profileData?.city}
                  />
                )
              }
            />
          </article>
        </aside>
      </section>
    </Section>
  );
};
